import { setCase } from "@@/utilities/casing";
import type { Translatable } from "../translations/translation";
import { translation } from "../translations/translation";

/**
 * Formats a given name string by trimming whitespace, splitting it into parts,
 * and converting each part to PascalCase. Returns the formatted name as a single string,
 * or `undefined` if the input is empty or only contains whitespace.
 *
 * @param name - The name string to format. Can be `undefined`.
 * @returns The formatted name in PascalCase, or `undefined` if input is invalid.
 */
const formatName = (name: string | undefined): string | undefined => {
    // If name is undefined, return undefined
    if (!name) return undefined;
    // Trim whitespace and check if empty
    const trimmedName = name.trim();
    // If trimmed name is empty, return undefined
    if (trimmedName.length === 0) return undefined;
    // Split by one or more whitespace, so multiple spaces are collapsed
    const parts = trimmedName.split(/\s+/);
    const outputParts = parts.map(part => {
        return setCase("pascalCase")(part);
    });
    // Join the parts back together with a space
    // and return the formatted name
    const formattedName = outputParts.join(" ");
    return formattedName;
};

/**
 * Returns a display name for a user or contact, based on available information and options.
 *
 * - If both first and last name are missing, returns the phone number (if allowed by options),
 *   otherwise returns a translation for "Anonymous".
 * - If only one of first or last name is present, returns that name.
 * - If both are present, returns either the full name or an abbreviated form (e.g. "Ema N."),
 *   depending on the showFullName option.
 *
 * @param input - An object containing firstName, lastName, and optionally phoneNumber.
 * @param options - Options to control display logic:
 *   - showFullName: If true, returns full name; otherwise, abbreviated.
 *   - showPhoneNumberInsteadOfAnonymous: If true, returns phone number if no name is available.
 * @returns A translatable string for display name.
 */
const getUserDisplayName = (
    input:
        | {
              firstName?: string | null;
              lastName?: string | null;
              phoneNumber?: string | null;
          }
        | undefined,
    options?: {
        showFullName?: boolean;
        showPhoneNumberInsteadOfAnonymous?: boolean;
    },
): Translatable => {
    // Format first and last names
    // (trims, PascalCase per word, returns undefined if empty)
    const showFullName = options?.showFullName ?? false;
    const firstName = formatName(input?.firstName || undefined);
    const lastName = formatName(input?.lastName || undefined);

    // If both names are missing,
    // optionally return phone number,
    // else return "Anonymous"
    if (!firstName && !lastName) {
        if (options?.showPhoneNumberInsteadOfAnonymous) {
            if (input && "phoneNumber" in input && input.phoneNumber?.trim()) {
                return input.phoneNumber.trim();
            }
        }
        return translation({
            sv: "Anonym",
            en: "Anonymous",
        });
    }

    // If only first name is present
    if (firstName && !lastName) {
        return firstName;
    }

    // If only last name is present
    if (lastName && !firstName) {
        return lastName;
    }

    if (!firstName || !lastName) {
        // this is just for type script, it can't happen since
        // we've already checked them above, even though typescript
        // doesn't understand that
        throw new Error("Missing first or last name");
    }

    return showFullName
        ? `${firstName} ${lastName}`
        : `${firstName} ${lastName[0].toLocaleUpperCase() + "."}`;
};

const userUtils = {
    formatName,
    getDisplayName: getUserDisplayName,
};

export { formatName, getUserDisplayName, userUtils };
